// frontend/src/lib/RiskEngine.js
// Compatibility facade.
// - Old pages import: calculateRobustRisk (AuditAssessment, MathLab, etc.)
// - Universal Audit imports: runUniversalRiskEngine (or imports from universalRiskEngine directly)
//
// This file keeps BOTH exports so nothing breaks.

export { runUniversalRiskEngine } from "./universalRiskEngine";

/**
 * Legacy export: calculateRobustRisk(answers, config)
 *
 * We do NOT re-implement your old full model here (that belongs in a separate legacy module).
 * But we MUST provide a stable function so older pages compile and render.
 *
 * This implementation is conservative, deterministic, and returns a superset of fields
 * that legacy UIs commonly read (math + summary-like fields).
 */
export function calculateRobustRisk(answers = {}, config = {}) {
  const annualRevenueEstimate = Number(
    answers?.annual_revenue_estimate ??
      answers?.annualRevenueEstimate ??
      config?.annualRevenueEstimate ??
      0
  );

  const peoplePerMonth = Number(
    answers?.people_per_month ??
      answers?.peoplePerMonth ??
      answers?.humans_impacted ??
      0
  );

  // Very conservative placeholder multipliers (stable + monotonic).
  const impactMultiplier = Math.max(1, Math.round(peoplePerMonth / 100) || 1);

  // Default “fine percent applied” bounded so it can’t explode by accident.
  // (This is just to keep legacy pages meaningful while we migrate them.)
  const finePctApplied = Math.min(0.25, Math.max(0.01, Number(config?.finePctApplied ?? 0.05)));

  const grossExposureCeilingUsd = Math.round(annualRevenueEstimate * finePctApplied * impactMultiplier);

  return {
    math: {
      // legacy-ish fields
      annual_revenue_estimate: annualRevenueEstimate,
      impact_multiplier: impactMultiplier,
      fine_percentage_applied: finePctApplied,

      // common headline field names that older pages may display
      computed_gross_exposure_ceiling: grossExposureCeilingUsd,
      computed_gross_exposure_ceiling_usd: grossExposureCeilingUsd,
    },

    // some UIs read these directly (not under math)
    annual_revenue_estimate: annualRevenueEstimate,
    computed_gross_exposure_ceiling: grossExposureCeilingUsd,
    computed_gross_exposure_ceiling_usd: grossExposureCeilingUsd,

    fine_selection: {
      worst_case_base_fine_pct: finePctApplied,
      fine_percentage_applied: finePctApplied,
    },

    flags: {
      legacy_placeholder_engine: true,
    },
  };
}
